/*
 * OC Recruitment — Packaging Sectors
 * Design: "The Boardroom" Corporate Modernism — Boutique Retained Search Firm
 * 8 simplified sectors with clean product photography
 */

import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";
import SEO from "@/components/SEO";
import { pageSEO, organizationSchema } from "@/lib/seoConfig";

const SECTORS_IMG =
  "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/packaging-products-alt-DnJyQJYFMuXMBqF4uvAzmc.webp";

const sectors = [
  {
    id: "rigid",
    slug: "rigid-plastics",
    title: "Rigid Plastics",
    description:
      "Bottles, containers, jars, and tubs manufactured through injection molding, blow molding, and thermoforming. We identify commercial talent with the technical knowledge and customer relationships to drive growth in this segment.",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-rigid-mvMgsT67azHQXtquiyVzSN.webp",
    alt: "Rigid plastic bottles and containers",
  },
  {
    id: "flexibles",
    slug: "flexible-packaging",
    title: "Flexible Packaging",
    description:
      "Films, pouches, bags, and wraps used across food, beverage, pharmaceutical, and consumer goods industries. We recruit sales professionals who understand barrier properties, substrate combinations, and the sustainability trends reshaping this segment.",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-flexible-cRxnuNze4ebgvqMYPQg5fX.webp",
    alt: "Flexible packaging pouches and films",
  },
  {
    id: "paper-corrugated",
    slug: "corrugated-packaging",
    title: "Corrugated Packaging",
    description:
      "From e-commerce shipping and retail-ready displays to folding cartons and industrial packaging. With e-commerce continuing to drive demand, strong commercial talent in paper and corrugated is more valuable than ever.",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-corrugated-v2-cx9aaRddU4AgpZMmZPwH7b.webp",
    alt: "Corrugated cardboard and paper packaging",
  },
  {
    id: "labels",
    slug: "labels-and-sleeves",
    title: "Labels & Sleeves",
    description:
      "Pressure-sensitive labels, shrink sleeves, in-mold labels, and cut-and-stack labels used across virtually every consumer product category. We identify sales talent who can articulate technical capabilities while building long-term customer partnerships.",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-labels-v2-UDnPqNi2SYX63T855gAvBY.webp",
    alt: "Labeled product bottles",
  },
  {
    id: "glass",
    slug: "glass-packaging",
    title: "Glass Packaging",
    description:
      "Serving the food, beverage, spirits, pharmaceutical, and cosmetic industries with a material valued for its premium perception, recyclability, and product protection. We recruit commercial professionals who can leverage these strengths to win and grow accounts.",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-glass-io6hk96t4qCiuxoLB9GbdR.webp",
    alt: "Glass bottles and jars",
  },
  {
    id: "metal",
    slug: "metal-packaging",
    title: "Metal Packaging",
    description:
      "Aluminum and steel cans, tins, aerosol containers, and closures used across food, beverage, and industrial applications. We connect packaging companies with commercial talent who can drive growth in this established and evolving segment.",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-metal-7KZGViCDGNpJzUE97Qvcdm.webp",
    alt: "Aluminum beverage cans",
  },
  {
    id: "folding-carton",
    slug: "folding-carton",
    title: "Folding Carton",
    description:
      "Folding cartons for food, beverage, pharmaceutical, cosmetic, and consumer goods packaging. From printed paperboard boxes to complex die-cut structures, we recruit sales professionals who understand substrate selection, print quality, and the brand-driven nature of this high-value segment.",
    image: "/manus-storage/folding-carton-packaging-optimized_96338168.webp",
    alt: "Premium folding carton boxes for consumer products",
  },
  {
    id: "caps-closures",
    slug: "caps-and-closures",
    title: "Caps & Closures",
    description:
      "Caps, closures, dispensing systems, and fitments serving food, beverage, personal care, and pharmaceutical markets. We recruit commercial talent who understand the technical precision and customer requirements of this specialized segment.",
    image: "https://d2xsxph8kpxj0f.cloudfront.net/310519663389532864/MeFhN5HSriTQgZdosXjPHR/sector-closures-bAGnetrxDcpH3Kx4dsKNYZ.webp",
    alt: "Bottle caps and closures",
  },
];

export default function PackagingSectors() {
  return (
    <div>
      <SEO
        title={pageSEO.packagingSectors.title}
        description={pageSEO.packagingSectors.description}
        keywords={pageSEO.packagingSectors.keywords}
        canonical={pageSEO.packagingSectors.canonical}
        structuredData={organizationSchema}
      />
      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={SECTORS_IMG}
            alt="Packaging sectors we recruit for - flexible, rigid, corrugated, folding carton, glass, metal, labels, closures"
            width={1600}
            height={1200}
            fetchPriority="high"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F2137]/95 via-[#0F2137]/80 to-[#0F2137]/60" />
        </div>
        <div className="container relative z-10 pt-32 pb-20">
          <div className="max-w-3xl">
            <ScrollReveal>
              <p className="text-steel text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                Packaging Sectors
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <h1 className="text-4xl sm:text-5xl text-white leading-[1.1] mb-6">
                Across the Full US Packaging Value Chain
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <p className="text-lg text-white/70 leading-relaxed">
                Our retained search expertise spans the full breadth of the
                packaging industry. We understand the unique commercial
                challenges and talent landscape within each segment.
              </p>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Sectors Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-8">
            {sectors.map((sector, i) => (
              <ScrollReveal key={sector.id} delay={i * 0.06}>
                <Link href={`/sectors/${sector.slug}`} className="group block bg-pale rounded border border-transparent hover:border-steel/20 hover:shadow-md transition-all duration-300 h-full overflow-hidden">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={sector.image}
                      alt={sector.alt}
                      loading="lazy"
                      width={640}
                      height={480}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0F2137]/50 to-transparent" />
                    <span className="absolute bottom-4 left-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/70">
                      0{i + 1}
                    </span>
                  </div>
                  <div className="p-6">
                    <h2 className="text-xl text-midnight mb-3 font-['DM_Serif_Display',Georgia,serif]">
                      {sector.title}
                    </h2>
                    <p className="text-slate-brand text-sm leading-relaxed">
                      {sector.description}
                    </p>
                    <span className="inline-flex items-center gap-1 text-steel text-xs font-semibold mt-4 group-hover:gap-2 transition-all">
                      Learn more <ArrowRight size={12} />
                    </span>
                  </div>
                </Link>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={0.4}>
            <p className="text-slate-brand leading-relaxed mt-12 max-w-4xl text-center mx-auto">
              Recruiting commercial talent across the full US packaging value chain,
              from flexible packaging and rigid plastics to labels & sleeves, caps & closures,
              corrugated, glass and metal packaging.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-midnight text-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl sm:text-4xl text-white mb-6">
                Need Commercial Talent in Your Packaging Segment?
              </h2>
            </ScrollReveal>
            <ScrollReveal delay={0.1}>
              <p className="text-lg text-white/70 leading-relaxed mb-10">
                Whatever your packaging segment, we have the network and
                specialist expertise to identify the commercial talent you need.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.2}>
              <Link
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-midnight text-sm font-semibold rounded hover:bg-white/90 transition-all duration-200 group"
              >
                Hire Packaging Sales Talent
                <ArrowRight
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </Link>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </div>
  );
}
